import { useEffect, useState, useRef } from "react";
import { DatePicker } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import CustomLabel from "@/components/inputs/CustomLabel";
import CustomSelect from "@/components/inputs/CustomSelect";
import DigitsDatePicker from "@/components/inputs/DigitsDatePicker";

import { MONTH, DAYS } from "@/constants/day";

dayjs.extend(customParseFormat);

const DEFAULT_TIME = "12:00";
const DATE_FORMAT = "DD.MM.YYYY";
const DATETIME_FORMAT = "DD.MM.YYYY HH:mm";

const InputCalendar = ({
  value,
  label,
  type,
  onSubmit,
  onKeyDown,
  onComplete,
  saveKeys = ["Enter"],
}) => {
  const [valueState, setValueState] = useState(null);
  const lastChangeRef = useRef({ val: null, ts: 0 });

  useEffect(() => {
    if (type === "year" && value) {
      setValueState(dayjs(`${value}-01-01`));
    } else if (type === "moment" && value) {
      setValueState(dayjs(value));
    } else if (type === "date" && value) {
      setValueState(dayjs(value));
    } else {
      setValueState(value);
    }
  }, [value, type]);

  const handleMomentBlur = (e) => {
    if (!(e.target instanceof HTMLInputElement)) return; // иначе отлавливает "ок" и стирает значение

    const rawValue = e.target.value?.trim();

    if (!rawValue) {
      const value = null;
      onSubmit?.(value);
      onKeyDown?.(e, value);
      return;
    }

    if (dayjs(rawValue, DATETIME_FORMAT, true).isValid()) {
      const value = dayjs(rawValue, DATETIME_FORMAT).toISOString();
      onSubmit?.(value);
      onKeyDown?.(e, value);
      return;
    }

    if (dayjs(rawValue, DATE_FORMAT, true).isValid()) {
      const withTime = dayjs(`${rawValue} ${DEFAULT_TIME}`, DATETIME_FORMAT);
      const value = withTime.toISOString();
      onSubmit?.(value);
      onKeyDown?.(e, value);
    }
  };

  const handleMomentChange = (date, dateString) => {
    const normalizedDate = dayjs(dateString, DATE_FORMAT, true).isValid()
      ? dayjs(date).hour(12).minute(0).second(0)
      : date;
    // Приводим дату к ISO, чтобы сравнение и отправка были стабильными
    const val = normalizedDate ? dayjs(normalizedDate).toISOString() : null;
    onSubmit?.(val);

    // Очищение даты
    if (!date) {
      onComplete?.(null);
      return;
    }

    // Защита от дублей: кнопка "Сейчас" и showTime могут триггерить onChange дважды
    const nowTs = Date.now();
    const isDuplicate =
      lastChangeRef.current.val === val &&
      nowTs - lastChangeRef.current.ts < 200;

    if (isDuplicate) return;

    lastChangeRef.current = { val, ts: nowTs };

    // Определяем клик по "Сейчас" по близости к текущему времени
    const now = dayjs();
    const isNowClick = Math.abs(dayjs(date).diff(now, "second")) <= 2;
    if (isNowClick) {
      onComplete?.(dayjs().toISOString());
    }
  };

  const handleDateBlur = (e) => {
    if (!(e.target instanceof HTMLInputElement)) return;

    const rawValue = e.target.value?.trim();

    if (!rawValue) {
      onSubmit?.(null);
      onKeyDown?.(e, null);
      return;
    }

    if (dayjs(rawValue, DATE_FORMAT, true).isValid()) {
      const normalized = dayjs(rawValue, DATE_FORMAT)
        .hour(0)
        .minute(0)
        .second(0);

      const val = normalized.format("YYYY-MM-DD");

      onSubmit?.(val);
      onKeyDown?.(e, val);
    }
  };

  const handleDateChange = (date) => {
    if (!date) {
      onSubmit?.(null);
      onComplete?.(null);
      return;
    }

    const normalized = dayjs(date).startOf("day");

    const val = normalized.format("YYYY-MM-DD");

    onSubmit?.(val);
    onComplete?.(val);
  };

  const renderSelect = (placeholder, options, onChange) => (
    <CustomSelect
      className="w-100"
      placeholder={placeholder}
      value={valueState}
      allowClear
      showSearch
      options={options}
      optionFilterProp="label"
      suffixIcon={<CalendarOutlined style={{ fontSize: 12 }} />}
      onChange={onChange}
      onInputKeyDown={(e) => {
        if (e.key === "Tab") {
          e.preventDefault();
          e.stopPropagation();
        }

        if (e.key !== "Enter") onKeyDown?.(e);
      }}
    />
  );

  return (
    <div className="custom-calendar-select">
      {label && <CustomLabel text={label} />}
      {type === "day" &&
        renderSelect(
          "Выберите день",
          DAYS,
          (data) => {
            onSubmit?.(data || null);
            onComplete?.(data || null);
          },
          onKeyDown,
        )}
      {type === "month" &&
        renderSelect(
          "Выберите месяц",
          MONTH,
          (data) => {
            onSubmit?.(data || null);
            onComplete?.(data || null);
          },
          onKeyDown,
        )}
      {type === "year" && (
        <DatePicker
          picker="year"
          value={valueState}
          format="YYYY"
          className="w-100"
          onChange={(date) => {
            const val = date ? Number(date.format("YYYY")) : null;
            onSubmit?.(val);
            onComplete?.(val);

            if (!date) {
              onComplete?.(null);
            }
          }}
          onKeyDown={(e) => {
            if (saveKeys.includes(e.key)) {
              const valueTrim = e.target.value?.trim();
              if (!valueTrim) {
                onSubmit?.(null);
                onKeyDown?.(e, null);
              } else if (dayjs(valueTrim, "YYYY", true).isValid()) {
                const val = Number(valueTrim);
                onSubmit?.(val);
                onKeyDown?.(e, val);
              }
            } else {
              onKeyDown?.(e);
            }
          }}
        />
      )}
      {type === "moment" && (
        <DigitsDatePicker
          type="moment"
          value={valueState}
          className="w-100"
          onChange={handleMomentChange}
          onBlur={handleMomentBlur}
          onOk={(date) => {
            const val = date ? dayjs(date).toISOString() : null;
            onComplete?.(val);
          }}
          onKeyDown={(e) => {
            if (saveKeys.includes(e.key)) {
              handleMomentBlur(e);
            } else {
              onKeyDown?.(e);
            }
          }}
        />
      )}
      {type === "date" && (
        <DigitsDatePicker
          value={valueState}
          className="w-100"
          onChange={handleDateChange}
          onBlur={handleDateBlur}
          onKeyDown={(e) => {
            if (saveKeys.includes(e.key)) {
              handleDateBlur(e);
            } else {
              onKeyDown?.(e);
            }
          }}
        />
      )}
    </div>
  );
};

export default InputCalendar;
